import { createAxiosInstance, SecurityErrorCode, type AxiosError } from '@template/api';
import { useGlobalDialogManager } from '@/composables/useGlobalDialogManager';
import { LocalStorageService, LocalStorageKey } from '@template/utils';
import { getBadRequestMessage, isDemoMode } from '@template/utils';
import { Logout, LogoutTask } from '@/services/auth.service';
import { useAccountStore } from '@template/stores';
import { useToastStore } from '@template/stores';
import { authService } from '@/services/api';
import router from '@/router';
import { watch } from 'vue';

const tokenResolver = () => LocalStorageService.getItem(LocalStorageKey.ACCESS_TOKEN) || undefined;

/**
 * 토큰 갱신 요청 여부 확인
 * @param url - 요청 URL
 * @returns refresh 요청이면 true
 */
const isRefreshRequestUrl = (url?: string): boolean => !!url?.includes('/auths/web/refresh');

/**
 * 로그아웃 요청 여부 확인
 * @param url - 요청 URL
 * @returns logout 요청이면 true
 *
 * 로그아웃 API의 401 응답에서 토큰 갱신을 다시 시도하면 무한 루프가 발생하므로
 * 별도 가드로 식별하기 위한 헬퍼입니다.
 */
const isLogoutRequestUrl = (url?: string): boolean =>
  !!url?.includes('/auths/logout') || !!url?.includes('/auths/logout-all');

/**
 * 계좌 인증 대기가 필요한 요청인지 확인
 * @param url - 요청 URL
 * @returns 대기가 필요하면 true, 아니면 false
 *
 * 계좌 인증 토큰 발급 API는 대기하지 않도록 제외
 */
const requiresAccountAuthWait = (url: string | undefined): boolean => {
  if (!url) return false;

  return !url.includes('/auths/account');
};

// Account Token Resolver: AccountStore에서 계좌 토큰 가져오기
// 주의: 계좌 인증 대기는 별도의 interceptor에서 처리합니다.
const accountTokenResolver = () => {
  const accountStore = useAccountStore();
  return accountStore.accountTokenComputed || undefined;
};

// 토큰 갱신 중 무한 루프 방지를 위한 플래그 및 대기 큐
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
  config: any;
}> = [];

// 빠른 인증 실패 시 재시도를 위한 플래그
let isWaitingForPassword = false;

// 계좌 토큰 검증 실패 처리 중 무한 루프 방지를 위한 플래그
let isHandlingAccountTokenValidation = false;

// 동일 메시지 토스트 중복 방지
const TOAST_DEBOUNCE_MS = 3000;
let lastToastMessage = '';
let lastToastTime = 0;

/**
 * 토스트 메시지 표시 헬퍼 함수
 * 동일 메시지는 일정 시간(TOAST_DEBOUNCE_MS) 내 중복 표시 방지
 * @param message - 표시할 메시지
 *
 */
const showToast = (message: string) => {
  const now = Date.now();
  if (message === lastToastMessage && now - lastToastTime < TOAST_DEBOUNCE_MS) {
    return;
  }
  lastToastMessage = message;
  lastToastTime = now;

  const toastStore = useToastStore();
  toastStore.addToast(message, 3000); // 에러 메시지는 3초간 표시
};

/**
 * 대기 중인 요청들을 재시도
 * @param error - 재시도할 에러를 전달하거나 null (성공 시)
 */
const processQueue = (error: AxiosError | null) => {
  if (error) {
    // refresh 실패 시 모든 요청 reject
    failedQueue.forEach((promise) => {
      promise.reject(error);
    });
  } else {
    // refresh 성공 시 모든 요청 재시도
    failedQueue.forEach((promise) => {
      api
        .request(promise.config)
        .then((value) => promise.resolve(value))
        .catch((err) => promise.reject(err));
    });
  }
  failedQueue = [];
};

/**
 * 토큰 갱신 및 요청 재시도 처리
 * @param error - Axios 에러 객체
 * @returns 재시도 요청 결과 또는 에러
 */
const handleTokenRefresh = async (error: AxiosError): Promise<any> => {
  const config = error.config;

  // 이미 refresh 중이면 큐에 추가하고 기다림
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject, config });
    });
  }

  const expiredAccessToken = tokenResolver();

  // 토큰이 없으면 로그아웃
  if (!expiredAccessToken) {
    showToast('다시 로그인 해주세요.');
    await LogoutTask();
    return Promise.reject(error);
  }

  isRefreshing = true;

  try {
    const result = await authService.refreshTokenForWeb({ expiredAccessToken });

    // 토큰 갱신 실패 시 로그아웃
    if (!result.data || !result.data.accessToken) {
      console.error('토큰 갱신 실패:', result);
      processQueue(error);
      showToast('다시 로그인 해주세요.');
      await Logout();
      return Promise.reject(error);
    }

    // 새 토큰 저장
    LocalStorageService.setItem(LocalStorageKey.ACCESS_TOKEN, result.data.accessToken);
    console.log('토큰 갱신 성공. 새 토큰이 저장되었습니다.');

    // 큐에 있는 요청들 재시도
    processQueue(null);

    // 첫 번째 요청 재시도
    if (config) {
      return api.request(config);
    }

    return Promise.reject(error);
  } catch (refreshError) {
    console.error('토큰 갱신 요청 실패:', refreshError);
    // errorHandler에서 이미 로그아웃한 경우(isRefreshing = false)가 아니면 큐 처리
    if (isRefreshing) {
      processQueue(error);
    }
    return Promise.reject(refreshError);
  } finally {
    isRefreshing = false;
  }
};

/**
 * 요청 파라미터 업데이트 헬퍼 함수
 * @param config - Axios 요청 설정
 * @param payload - 다이얼로그에서 받은 인증 정보
 * @returns 업데이트된 요청 설정
 */
const updateRequestParams = (
  config: any,
  payload: { quickAuthEnabled: boolean; password?: string }
): any => {
  const updatedConfig = { ...config };

  // 요청 body 데이터 업데이트
  if (updatedConfig.data) {
    try {
      const requestData =
        typeof updatedConfig.data === 'string'
          ? JSON.parse(updatedConfig.data)
          : updatedConfig.data;

      // quickAuthEnabled와 accountPassword 업데이트
      if (requestData && typeof requestData === 'object') {
        requestData.quickAuthEnabled = payload.quickAuthEnabled;
        requestData.accountPassword = payload.password;

        updatedConfig.data =
          typeof config.data === 'string' ? JSON.stringify(requestData) : requestData;
      }
    } catch (parseError) {
      console.error('[Axios] 요청 데이터 파싱 실패:', parseError);
    }
  }

  // URL 파라미터 업데이트 (params가 있는 경우)
  if (updatedConfig.params && typeof updatedConfig.params === 'object') {
    updatedConfig.params.quickAuthEnabled = payload.quickAuthEnabled;
    if (payload.password) {
      updatedConfig.params.accountPassword = payload.password;
    }
  }

  return updatedConfig;
};

/**
 * 빠른 인증 실패 처리 및 요청 재시도
 * @param error - Axios 에러 객체
 * @returns 재시도 요청 결과 또는 에러
 */
const handleQuickAuthFailure = async (error: AxiosError): Promise<any> => {
  const config = error.config;

  if (!config) {
    return Promise.reject(error);
  }

  // 이미 비밀번호 입력 대기 중이면 에러 반환
  if (isWaitingForPassword) {
    return Promise.reject(error);
  }

  isWaitingForPassword = true;
  const accountStore = useAccountStore();

  accountStore.setAccountSimpleAuthEnabled(false);

  const dialogManager = useGlobalDialogManager();
  const accountPasswordState = dialogManager.getDialogState('accountPassword');

  return new Promise((resolve, reject) => {
    let isResolved = false;

    // 다이얼로그가 닫혔을 때 감지 (취소, ESC, 오버레이 클릭 등)
    const stopWatcher = watch(
      accountPasswordState,
      (isOpen) => {
        if (!isOpen && !isResolved) {
          isResolved = true;
          isWaitingForPassword = false;
          stopWatcher();
          // 사용자가 다이얼로그를 닫은 경우 조용히 종료
          return;
        }
      },
      { immediate: false }
    );

    // 다이얼로그 열기
    dialogManager.open('accountPassword', (payload) => {
      const accountStore = useAccountStore();

      // 요청 파라미터 업데이트 및 재시도
      const updatedConfig = updateRequestParams(config, payload);

      // 요청 재시도
      api
        .request(updatedConfig)
        .then((value) => {
          if (!isResolved) {
            isResolved = true;
            stopWatcher();

            // 빠른 인증 활성화 시 localStorage 저장
            if (payload.quickAuthEnabled) {
              accountStore.setAccountSimpleAuthEnabled(true);
            }

            resolve(value);
            accountStore.resetAccountPasswordFailedCount();
            dialogManager.close('accountPassword');
            isWaitingForPassword = false;
          }
        })
        .catch((retryError) => {
          if (!isResolved) {
            // 비밀번호 오류인 경우 다이얼로그를 열어둔 채로 재입력 가능하도록 함
            const errorData = retryError as any;
            if (errorData?.code === 'ACCOUNT_PASSWORD_MISMATCH') {
              // 비밀번호 오류는 다이얼로그를 닫지 않고 재입력 가능하도록 함
              // isResolved를 true로 설정하지 않아서 다음 시도에서도 처리 가능
              return;
            }

            // 비밀번호 오류가 아닌 다른 에러인 경우에만 종료 처리
            isResolved = true;
            stopWatcher();
            dialogManager.close('accountPassword');
            reject(retryError);
            isWaitingForPassword = false;
          }
        });
    });
  });
};

/**
 * Axios 에러 핸들러
 * @param error - Axios 에러 객체
 *
 * FIXME: 임시 처리로, 추후 개선 필요
 * - 에러 응답의 message 필드를 우선적으로 표시
 * - 에러 코드별 다국어 메시지 매핑
 * - 재시도 가능한 에러에 대한 재시도 로직
 * - 네트워크 에러 시 오프라인 모드 전환
 */
const errorHandler = async (error: AxiosError): Promise<any> => {
  if (error.response) {
    const responseData = error.response.data as { code?: string; [key: string]: any };
    // 서버에서 에러 응답을 받은 경우 (상태 코드 포함)
    switch (error.response.status) {
      case 400:
        // Bad Request 처리
        const badRequestData = responseData;
        const errorMessage = getBadRequestMessage(badRequestData);
        if (badRequestData.code === SecurityErrorCode.QUICK_AUTH_FAILED.code) {
          // 빠른 인증 실패 시 다이얼로그 열고 재시도
          return handleQuickAuthFailure(error);
        }

        if (badRequestData.code === 'H0006' && badRequestData.detail.includes('비밀번호 오류')) {
          return new Promise((resolve, reject) => {
            const accountStore = useAccountStore();
            const match = badRequestData.detail.match(/오류횟수\s*:\s*(\d+)/);
            const errorCount = match ? parseInt(match[1]) : 0;
            accountStore.setAccountPasswordFailedCount(errorCount);

            reject({ code: 'ACCOUNT_PASSWORD_MISMATCH', detail: badRequestData.detail });
          });
        }
        if (
          badRequestData.title === 'MEMBER_NOT_APPROVED' ||
          badRequestData.title === 'MEMBER_DOCUMENTS_NOT_SUBMITTED'
        ) {
          break;
        }
        showToast(errorMessage);
        console.error('[Axios] 400 Bad Request:', responseData);
        break;
      case 401:
        const requestUrl = error.config?.url;
        const isRefreshRequest = isRefreshRequestUrl(requestUrl);
        const isLogoutRequest = isLogoutRequestUrl(requestUrl);

        // 로그아웃 API 401은 refresh 시도 없이 즉시 로컬 로그아웃 처리
        if (isLogoutRequest) {
          await LogoutTask();
          return Promise.reject(error);
        }

        if (responseData.code === SecurityErrorCode.UNAUTHORIZED.code) {
          await LogoutTask();
          return Promise.reject(error);
        }

        // refresh 요청 자체가 401이고 액세스토큰이 유효하지 않은 경우 로그아웃
        // 액세스토큰이 유효해서 refresh 요청 실패할 경우 무시
        if (
          isRefreshRequest &&
          responseData.code !== SecurityErrorCode.ACCESS_TOKEN_STILL_VALID.code
        ) {
          console.error('토큰 갱신 요청이 401 에러. 로그아웃 처리합니다.');
          isRefreshing = false; // 플래그 해제
          // 큐에 쌓인 요청들 reject
          processQueue(error);
          showToast('세션이 만료되었습니다. \n 다시 로그인 해주세요.');
          await LogoutTask();
          return Promise.reject(error);
        }
        // 토큰 갱신이 필요한 경우
        const needsTokenRefresh =
          !isRefreshRequest &&
          !isLogoutRequest &&
          (responseData.code === SecurityErrorCode.TOKEN_VALIDATION_FAILED.code ||
            responseData.code === SecurityErrorCode.COMMUNITY_ACCESS_TOKEN_EXPIRED.code);

        if (needsTokenRefresh) {
          return handleTokenRefresh(error);
        }
        // 이메일 또는 비밀번호 오류 시 메시지 표시
        if (
          responseData.code === SecurityErrorCode.EMAIL_NOT_FOUND.code ||
          responseData.code === SecurityErrorCode.INVALID_PASSWORD.code ||
          responseData.code === SecurityErrorCode.EXPIRED_QUICK_AUTH.code
        ) {
          showToast(responseData.detail);
        }

        if (responseData.code === SecurityErrorCode.ACCOUNT_TOKEN_VALIDATION_FAILED.code) {
          const config = error.config;

          // 이미 처리 중이면 그냥 reject (처리하지 않음)
          if (isHandlingAccountTokenValidation) {
            return Promise.reject(error);
          }

          isHandlingAccountTokenValidation = true;
          const accountStore = useAccountStore();

          try {
            // 계좌 토큰 검증 실패 처리
            await accountStore.handleAccountTokenValidationFailure();

            // 첫 번째 요청 재시도
            if (config) {
              return api.request(config);
            }

            return Promise.reject(error);
          } catch (validationError) {
            console.error('계좌 토큰 검증 실패 처리 중 오류:', validationError);
            return Promise.reject(validationError);
          } finally {
            isHandlingAccountTokenValidation = false;
          }
        }
        // FIXME: 401 에러의 다양한 케이스 처리 필요 (세션 만료, 권한 없음 등)
        break;
      case 403:
        // Forbidden 처리
        // FIXME: 권한별 상세 메시지 필요 (읽기 권한, 쓰기 권한 등)
        showToast('접근 권한이 없습니다.');
        console.error('[Axios] 403 Forbidden:', responseData);
        break;
      case 404:
        // Not Found 처리
        // FIXME: API 엔드포인트 오류인지, 리소스 부재인지 구분 필요
        showToast('요청한 리소스를 찾을 수 없습니다.');
        console.error('[Axios] 404 Not Found:', responseData);
        await router.push({ name: 'not-found' });
        return;
      case 500: {
        const isLogoutRequest = isLogoutRequestUrl(error.config?.url);
        const isRefreshRequest = isRefreshRequestUrl(error.config?.url);
        if (isRefreshRequest || isLogoutRequest) {
          return await LogoutTask();
        }

        // Internal Server Error 처리
        // FIXME: 서버 에러 시 자동 재시도 또는 에러 리포트 전송 고려
        showToast('서버 오류가 발생했습니다.');
        console.error('[Axios] 500 Internal Server Error:', responseData);
        // await router.push({ name: 'error' });
        break;
      }
      default:
        // FIXME: 다른 상태 코드(502, 503, 504 등)에 대한 처리 추가 필요
        showToast('알 수 없는 에러가 발생했습니다.');
        console.error('[Axios] 기타 에러:', error.response.status, responseData);
    }
  } else if (error.request) {
    // 요청이 전송되었지만 응답을 받지 못한 경우 (네트워크 문제 등)
    // FIXME: 네트워크 끊김 감지 및 재연결 로직 필요
    showToast('서버와 연결할 수 없습니다.');
    console.error('[Axios] 네트워크 에러 - 응답 없음:', error.request);
  } else {
    // 요청 설정 중 에러가 발생한 경우
    // FIXME: 클라이언트 측 설정 오류에 대한 상세 로깅 필요
    showToast('요청 처리 중 오류가 발생했습니다.');
    console.error('[Axios] 요청 설정 에러:', error.message);
  }
  return Promise.reject(error); // 에러를 다시 reject하여 호출한 곳에서 처리할 수 있도록 함
};

const api = createAxiosInstance(tokenResolver, errorHandler, undefined, accountTokenResolver);

/** 로그인 등 TRADE_MODE 저장 전 요청에서 실/모의 서버 지정 시 사용 */
export interface DemoModeRequestConfig {
  useDemoServer?: boolean;
}

/** api.post 세 번째 인자 타입. useDemoServer 등 앱 전용 옵션 전달 시 캐스팅용 */
export type ApiRequestConfig = Parameters<typeof api.post>[2];

/**
 * Request 인터셉터
 *
 * 1. 계좌 인증 대기 처리
 * 2. 거래 모드에 따른 URL prefix 추가
 *    - config.useDemoServer가 있으면 해당 값 사용 (로그인 시 체크박스 기준)
 *    - 없으면 isDemoMode()(localStorage) 사용
 */
api.interceptors.request.use(
  async (config) => {
    // 1. 계좌 인증 대기 처리
    if (requiresAccountAuthWait(config.url)) {
      const accountStore = useAccountStore();
      if (accountStore.isAuthenticating && accountStore.authenticationPromise) {
        await accountStore.authenticationPromise;
      }
    }

    // 2. 거래 모드에 따라 URL prefix
    const useDemo =
      (config as DemoModeRequestConfig).useDemoServer !== undefined
        ? (config as DemoModeRequestConfig).useDemoServer
        : isDemoMode();
    if (useDemo && config.url) {
      config.url = config.url.replace('/main/v1', '/demo/v1');
    }

    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

/**
 * Axios 인터셉터 상태 초기화
 * 로그아웃 시 호출하여 토큰 갱신 관련 상태를 초기화합니다.
 */
export const resetAxiosInterceptorState = () => {
  // 토큰 갱신 중인 요청들을 모두 reject
  if (failedQueue.length > 0) {
    const error = new Error('로그아웃으로 인한 요청 취소');
    failedQueue.forEach((promise) => {
      promise.reject(error);
    });
    failedQueue = [];
  }

  // 토큰 갱신 플래그 초기화
  isRefreshing = false;

  // 비밀번호 입력 대기 플래그 초기화
  isWaitingForPassword = false;

  // 계좌 토큰 검증 실패 처리 플래그 초기화
  isHandlingAccountTokenValidation = false;
};

export default api;
